/**
 * Keep a free-text field number-only as the user types: digits, at most one
 * decimal point, and at most one leading minus sign survive; everything else
 * (letters, stray symbols, newlines from a paste) is dropped.
 *
 * This is what stands in for `<input type="number">`'s own filtering once a
 * field has to be a `<textarea>` in order to wrap — see `AutoGrowTextarea`'s
 * `numeric` prop and `MoneyInput`.
 */
export function sanitizeNumericText(input: string): string {
  // Remove everything except digits, dot, and minus.
  let s = input.replace(/[^0-9.-]/g, '');
  // Only a single leading minus is allowed.
  const negative = s.startsWith('-');
  s = s.replace(/-/g, '');
  // Collapse to a single decimal point (keep the first).
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  return (negative ? '-' : '') + s;
}

/**
 * Keep a free-text field number-only, PLUS a single trailing `%`, as the user
 * types — for a dual-mode field where the same box accepts either a fixed
 * amount ("45.00") or a percentage ("10%"), distinguished only by that
 * trailing symbol (Xero-style line-item discount entry).
 *
 * Unlike {@link sanitizeNumericText}, a leading minus is never kept — a
 * discount (amount or percentage) is never negative. `%` may appear anywhere
 * in the raw input (typed mid-string, pasted); if present at all, exactly one
 * survives, moved to the end, so "1%0" behaves the same as "10%" while typing.
 */
export function sanitizeDiscountText(input: string): string {
  const hasPercent = input.includes('%');
  // Remove everything except digits and dot (no minus — never negative).
  let s = input.replace(/[^0-9.]/g, '');
  // Collapse to a single decimal point (keep the first).
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  return hasPercent ? `${s}%` : s;
}
